import { setPlayers } from "./players";
import { adminReply, setAdminHello } from "./admin";
import { ByteReader } from "../replay/bytes";
import type { Header } from "../replay/format";
import { gunzip, parseStatics, type Statics } from "../replay/replay";
import { WorldState } from "./world";

/*
 * The live world: a WebSocket stream in the replay format (a hello with the
 * header, the static map, a keyframe, then a delta per frame, each followed by
 * its summary row), played into a `WorldState` in real time. Shaped like
 * `Playback`, so the renderer, timeline and panels work unchanged.
 */

/** The static map last unpacked, kept across connections (a reconnect after
 *  the server restarts gets the same blob again, megabytes on a big world):
 *  its compressed bytes' length and hash, the world it belongs to, and the
 *  unpacked layers. The same bytes again: the kept layers, nothing unpacked,
 *  and the renderer sees the very same arrays (so its ground is reused). */
let keptStatics: { len: number; hash: number; world: string; statics: Statics } | null = null;

/** FNV-1a over every byte (a few ms for a few MB). */
function hashBytes(b: Uint8Array): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < b.length; i++) h = Math.imul(h ^ b[i], 0x01000193);
  return h >>> 0;
}

const worldKey = (h: Header) => `${h.title}|${h.world.width}x${h.world.height}`;

/** The map this tab holds, for a reconnect's address (`have=len-hash`): a
 *  server that knows it can answer `statics: false` and skip the blob. */
export function staticsHeld(): string | null {
  return keptStatics ? `${keptStatics.len}-${keptStatics.hash.toString(16)}` : null;
}

/** Seconds the display stays behind the newest frame, so motion can
 *  interpolate and network jitter doesn't stall it; and how far behind it may
 *  fall before snapping forward (a stall, a background tab). */
const BEHIND_S = 0.3;
const SNAP_S = 2;

export class LivePlayback {
  readonly state: WorldState;
  time = 0;
  playing = true;
  speed = 1;
  buffering = false;
  loop = false;
  /** Frames received but not applied yet. */
  private queue: Uint8Array[] = [];
  /** Whether a keyframe has come: deltas before one have no base, so they're
   *  dropped (and a keyframe asked for). */
  private keyed = false;
  private listeners: ((jump: boolean) => void)[] = [];
  /** Shaped like a replay file for the timeline (frames arrive as they come). */
  readonly replay = { framesArrived: () => this.header.frames };

  constructor(
    readonly header: Header,
    statics: Statics,
    private socket: LiveSocket,
  ) {
    this.state = new WorldState(header, statics);
  }

  get frames(): number {
    return this.header.frames;
  }

  get frameRate(): number {
    return this.header.tick_rate / this.header.frame_interval;
  }

  get alpha(): number {
    return Math.max(0, Math.min(1, this.time - (this.state.frame - 1)));
  }

  onFrame(f: (jump: boolean) => void): void {
    this.listeners.push(f);
  }

  /** A frame from the server (and its summary row). */
  push(bytes: Uint8Array): void {
    if (!this.keyed) {
      if (bytes[4] !== 1) {
        this.socket.wantKey();
        return;
      }
      this.keyed = true;
    }
    this.queue.push(bytes);
    this.header.frames += 1;
  }

  addRow(row: { alive: number[]; in_contact: number[]; dry_in_contact: number[]; suppressed: number[]; deaths: number[]; shells_in_flight: number }): void {
    const s = this.header.summary;
    s.alive.push(row.alive);
    s.in_contact.push(row.in_contact);
    s.dry_in_contact.push(row.dry_in_contact);
    s.suppressed.push(row.suppressed);
    s.deaths.push(row.deaths);
    s.shells_in_flight.push(row.shells_in_flight);
  }

  tick(dt: number): void {
    // Nothing shown yet (a join, a reconnect): the keyframe at once, not a
    // frame later. Holding back is for smooth motion, and nothing moves yet.
    if (this.state.frame < 0 && this.queue.length) {
      this.state.apply(new ByteReader(this.queue.shift()!));
      this.time = this.state.frame;
      this.state.index();
      for (const f of this.listeners) f(true);
      return;
    }
    const newest = this.state.frame + this.queue.length;
    const behind = Math.max(1, Math.round(BEHIND_S * this.frameRate));
    // Real time; snap forward if far behind.
    this.time = Math.max(this.time + dt * this.frameRate, newest - behind - SNAP_S * this.frameRate);
    this.time = Math.min(this.time, newest - behind + 0.999);
    let applied = false;
    let jump = false;
    while (this.queue.length && this.time > this.state.frame) {
      const bytes = this.queue.shift()!;
      jump ||= bytes[4] === 1 && this.state.frame >= 0;
      this.state.apply(new ByteReader(bytes));
      applied = true;
    }
    this.buffering = this.queue.length === 0 && this.time >= this.state.frame;
    if (applied) {
      this.state.index();
      for (const f of this.listeners) f(jump);
    }
  }

  /** Live: there's only now. */
  async seek(_frame: number): Promise<void> {}

  async step(_delta: number): Promise<void> {}

  /** Sends a command (see the server's protocol notes). */
  send(command: object): void {
    this.socket.send(command);
  }

  bake(spec: object): Promise<Record<string, unknown>> {
    return this.socket.bake(spec);
  }

  bakePolicy(yaml: string): Promise<Record<string, unknown>> {
    return this.socket.bakePolicy(yaml);
  }
}

/** The connection: hands frames to the playback once it exists. */
/** How a battle ended: the result line, the winning side (none: time's
 *  up), the published replay, whether the next battle follows (live world),
 *  and the side this client played (255: watching). */
export interface Ended {
  result: string;
  winner: number | null;
  replay: string | null;
  next: boolean;
  faction: number;
}

export class LiveSocket {
  private ws: WebSocket;
  playback: LivePlayback | null = null;
  /** Frames that arrived while the map was being unpacked. */
  private pending: Uint8Array[] = [];
  private keyAsked = 0;
  private ended = false;
  /** The commander's own state (the server's `state` message, every 5 s):
   * population, research, designs, construction, market, territory, intel.
   * Kept for the interface; nothing reads it yet. */
  playerState: Record<string, unknown> | null = null;
  /** The static map last sent (kept for hellos that don't send it again). */
  private statics: Statics | null = null;
  /** The server's mine plans, as they come. */
  onPlan?: (m: Record<string, unknown>) => void;
  /** Called with each new state message. */
  onState: ((state: Record<string, unknown>) => void) | null = null;

  constructor(
    url: string,
    /** Called with each new world (the first hello, and when it's resized). */
    private onWorld: (header: Header, statics: Statics, faction: number) => void,
    private onClose: (reason: string) => void,
    /** The battle ended (the results screen). */
    private onEnded: (ended: Ended) => void = () => {},
    /** The map is on its way (after the hello). */
    private onHello: () => void = () => {},
  ) {
    this.ws = new WebSocket(url);
    this.ws.binaryType = "arraybuffer";
    let header: Header | null = null;
    let faction = 0;
    this.ws.onmessage = (e) => {
      if (typeof e.data === "string") {
        const m = JSON.parse(e.data);
        if (m.type === "hello") {
          header = m.header as Header;
          header.frames = 0;
          header.summary = { alive: [], in_contact: [], dry_in_contact: [], suppressed: [], deaths: [], shells_in_flight: [] };
          faction = m.faction;
          // Who holds each side (signed-in commanders), for everyone,
          // watchers too; the state's list takes over for a commander.
          setPlayers(m);
          setAdminHello(m);
          this.playback = null;
          this.onHello();
          // A skirmish: put its room in the address, so a reload rejoins it.
          if (typeof m.room === "string" && m.room !== "live") {
            const params = new URLSearchParams(location.search);
            params.delete("skirmish");
            params.set("room", m.room);
            history.replaceState(null, "", `?${params.toString().replace("live=&", "live&").replace(/^live=$/, "live")}`);
          }
          this.pending = [];
          // The map unchanged (`statics: false`, asked for with
          // caps=statics): no blob follows; the world goes on with the one
          // already here (a big map's is megabytes, every structure built).
          // A fresh connection after a drop: the map kept from before.
          const held = this.statics ?? (keptStatics && keptStatics.world === worldKey(header) ? keptStatics.statics : null);
          if (m.statics === false && held) {
            this.statics = held;
            const [h, st] = [header, held];
            queueMicrotask(() => this.onWorld(h, st, faction));
          }
        } else if (m.type === "row") {
          this.playback?.addRow(m);
        } else if (m.type === "state") {
          this.playerState = m;
          this.onState?.(m);
        } else if (m.type === "plan") {
          this.onPlan?.(m);
        } else if (m.type === "admin") {
          adminReply(m);
        } else if (m.type === "policy_baked") {
          this.policyBaking.shift()?.(m);
        } else if (m.type === "baked") {
          // Replies come in order: the oldest waiting request is this one's.
          this.baking.shift()?.(m);
        } else if (m.type === "ended") {
          this.ended = true;
          this.onEnded({ ...(m as Ended), faction });
        }
        return;
      }
      const b = new Uint8Array(e.data);
      if (b[0] === 1 && header) {
        const h = header;
        const body = b.subarray(1);
        const hash = hashBytes(body);
        const k = keptStatics;
        if (k && k.len === body.length && k.hash === hash && k.world === worldKey(h)) {
          // The map this tab already has (a reconnect): nothing to unpack.
          this.statics = k.statics;
          queueMicrotask(() => this.onWorld(h, k.statics, faction));
          return;
        }
        void gunzip(body).then((raw) => {
          const statics = parseStatics(h, raw);
          this.statics = statics;
          keptStatics = { len: body.length, hash, world: worldKey(h), statics };
          this.onWorld(h, statics, faction);
        });
      } else if (b[0] === 2) {
        if (this.playback) this.playback.push(b.subarray(1));
        else if (header) this.pending.push(b.subarray(1));
      }
    };
    // After a skirmish ends the room closes: the results screen says so.
    // Once per connection (a failed one fires both error and close).
    let told = false;
    const close = (why: string) => {
      if (told || this.ended || this.closed) return;
      told = true;
      this.onClose(why);
    };
    this.ws.onclose = (e) => close(e.reason || "the connection closed");
    this.ws.onerror = () => close("couldn't connect");
  }

  /** Hands frames to a new world's playback, those that came early first. */
  attach(playback: LivePlayback): void {
    this.playback = playback;
    for (const f of this.pending) playback.push(f);
    this.pending = [];
  }

  /** Asks for a keyframe (at most once a second). */
  wantKey(): void {
    const now = performance.now();
    if (now - this.keyAsked < 1000) return;
    this.keyAsked = now;
    this.send({ type: "key" });
  }

  send(command: object): void {
    if (this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(command));
  }

  /** Requests waiting for a design preview, oldest first. */
  private baking: ((reply: Record<string, unknown>) => void)[] = [];

  /** A design's preview (stats, costs, research it needs, or what's wrong),
   *  without registering it. */
  bake(spec: object): Promise<Record<string, unknown>> {
    return new Promise((resolve) => {
      if (this.ws.readyState !== WebSocket.OPEN) return resolve({ error: "not connected" });
      this.baking.push(resolve);
      this.ws.send(JSON.stringify({ type: "bake", spec }));
    });
  }

  /** Policy checks waiting, oldest first (replies come in order). */
  private policyBaking: ((reply: Record<string, unknown>) => void)[] = [];

  /** A policy's cost against the budget, or what's wrong with it. */
  bakePolicy(yaml: string): Promise<Record<string, unknown>> {
    return new Promise((resolve) => {
      if (this.ws.readyState !== WebSocket.OPEN) return resolve({ ok: false, budget: 200, error: "Not connected" });
      this.policyBaking.push(resolve);
      this.ws.send(JSON.stringify({ type: "bake_policy", policy: yaml }));
    });
  }

  /** Closed on purpose (leaving): not a drop to reconnect from. */
  private closed = false;

  close(): void {
    this.closed = true;
    this.ws.close();
  }
}
